import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

/**
 * Download a relativity-produced report (metadata sync, parent/child relationships,
 * load file import) by its audit trail id. The report is stored durably in S3 as an
 * audit-trail artefact; this hits the relativity artefact download route (guarded by
 * RelativityImport + matter access) so it works for the same users who can run the flows.
 */
export const useDownloadRelativityReportArtefact = (matterId: string) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadReportArtefact = async (auditTrailId: string) => {
    const response = await fetch(
      `${APIBaseChronos}/api/relativity/artefact/${auditTrailId}/download?matterId=${matterId}`,
      getFetchConfig({ method: FetchMethod.GET }),
    );

    if (!response.ok) {
      throw new Error('Failed to download report');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    const filename = filenameMatch?.[1] ?? 'relativity-report.csv';
    const blob = await response.blob();
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: filename,
    });

    link.click();
    URL.revokeObjectURL(link.href);
  };

  const { mutate: fetchDownload, isLoading: isLoadingDownload } = useMutation(downloadReportArtefact, {
    onError: () => {
      toast.error('Failed to download report. Please try again.');
    },
  });

  return { fetchDownload, isLoadingDownload };
};
